import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Wallet, AlertTriangle } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * My Collection — a sales person's own ledger.
 *
 * The same figures the office reads on Ledger → Summary (opening, debit, credit, closing) plus the
 * ageing buckets, but only for the clients assigned to whoever is logged in. The scope is forced on
 * the SERVER from the JWT, not from anything this page sends, which is what makes it safe to show
 * to someone whose role has no Ledger permission at all.
 */

const money = (n?: number | null) =>
  `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Row {
  merchantId: number; merchantName: string; gstin: string;
  accountGroupId: number | null; accountGroupName: string | null;
  openingBalance: number; totalDebit: number; totalCredit: number;
  closingBalance: number; closingDebit: number; closingCredit: number;
}
interface Res {
  clientCount: number;
  range: { from: string; to: string };
  asAt: string;
  rows: Row[];
  totals: { openingBalance: number; totalDebit: number; totalCredit: number; closingDebit: number; closingCredit: number; closingBalance: number };
  ageing: {
    buckets: { key: string; label: string }[];
    debit: number[]; credit: number[]; debitTotal: number; creditTotal: number; net: number; oldestDays: number;
    clients: { merchantId: number; merchantName: string; debit: number[]; debitTotal: number; creditTotal: number; net: number; oldestDays: number }[];
  };
}

function monthStart() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function monthEnd() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10); }

export function MyCollectionPage() {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(monthEnd());
  const [q, setQ] = useState('');

  const params = useMemo(() => ({ fromDate, toDate, ...(q ? { q } : {}) }), [fromDate, toDate, q]);

  const { data, isLoading } = useQuery<Res>({
    queryKey: ['my-collection', params],
    queryFn: async () => (await api.get<Res>('/ledger/my-collection', { params })).data,
    enabled: !!fromDate && !!toDate && fromDate <= toDate,
  });

  const rows = data?.rows ?? [];
  const t = data?.totals;
  const ag = data?.ageing;
  const lastBucket = (ag?.buckets.length ?? 1) - 1;

  const handleExport = async () => {
    const qs = new URLSearchParams(params as any).toString();
    await downloadBlob(`/ledger/my-collection/export?${qs}`, `my-collection-${fromDate}-to-${toDate}.csv`);
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Wallet size={20} className="text-emerald-600" /> My Collection</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ? `${`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()} · ` : ''}What your own clients owe — the same figures as the office ledger, for your accounts only.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block">Search client</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Company or GST" className="border rounded px-2 py-1.5 text-sm dark:bg-slate-900" />
          </div>
          <button onClick={handleExport} disabled={!rows.length}
            className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1 disabled:opacity-40">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : data && data.clientCount === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded p-6 text-center">
          <AlertTriangle className="mx-auto text-amber-500 mb-2" size={22} />
          <div className="text-sm font-medium text-amber-900">No clients are assigned to you yet</div>
          <p className="text-xs text-amber-700 mt-1 max-w-md mx-auto">
            This page shows the accounts where you are set as the sale person. Ask the admin team to set you as the
            sale person on your merchants (Merchants → edit → Sale Person) and they will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            {[
              { label: 'My clients', value: (data?.clientCount ?? 0).toLocaleString('en-IN'), tone: '' },
              { label: 'Opening', value: money(t?.openingBalance), tone: 'text-slate-700' },
              { label: 'Debit', value: money(t?.totalDebit), tone: 'text-blue-700' },
              { label: 'Credit', value: money(t?.totalCredit), tone: 'text-emerald-700' },
              { label: 'Closing (owed to us)', value: money(t?.closingDebit), tone: 'text-blue-800' },
              { label: 'Closing (we hold)', value: money(t?.closingCredit), tone: 'text-emerald-800' },
            ].map((c) => (
              <div key={c.label} className="bg-white dark:bg-slate-900 border rounded p-3">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">{c.label}</div>
                <div className={`text-lg font-semibold mt-0.5 tabular-nums ${c.tone}`}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* How old the money is — the same buckets as the office ageing sheet. */}
          {ag && (
            <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden mb-4">
              <div className="px-4 py-2 border-b bg-slate-50 dark:bg-slate-800 flex items-baseline justify-between">
                <span className="text-sm font-semibold">How old is my money</span>
                <span className="text-[11px] text-gray-500">
                  as at {data?.asAt} · oldest {ag.oldestDays ? `${ag.oldestDays} days` : '—'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-emerald-50 dark:bg-slate-800 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left"> </th>
                      {ag.buckets.map((b) => <th key={b.key} className="px-3 py-2 text-right">{b.label}</th>)}
                      <th className="px-3 py-2 text-right border-l">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="px-3 py-2 text-blue-700 font-medium">Debit (owed)</td>
                      {ag.debit.map((v, i) => (
                        <td key={i} className={`px-3 py-2 text-right tabular-nums ${i === lastBucket && v ? 'text-rose-700 font-medium' : 'text-gray-600'}`}>{v ? money(v) : ''}</td>
                      ))}
                      <td className="px-3 py-2 text-right tabular-nums border-l font-medium">{money(ag.debitTotal)}</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-3 py-2 text-emerald-700 font-medium">Credit (paid)</td>
                      {ag.credit.map((v, i) => <td key={i} className="px-3 py-2 text-right tabular-nums text-gray-600">{v ? money(v) : ''}</td>)}
                      <td className="px-3 py-2 text-right tabular-nums border-l font-medium">{money(ag.creditTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
            <div className="px-4 py-2 border-b bg-slate-50 dark:bg-slate-800 text-sm font-semibold">
              My clients · closing balance {data?.range ? `(${data.range.from} to ${data.range.to})` : ''}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-emerald-50 dark:bg-slate-800 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-left">GST No.</th>
                    <th className="px-3 py-2 text-right">Opening</th>
                    <th className="px-3 py-2 text-right">Debit</th>
                    <th className="px-3 py-2 text-right">Credit</th>
                    <th className="px-3 py-2 text-right">Closing Debit</th>
                    <th className="px-3 py-2 text-right">Closing Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No client had a balance or any activity in this period.</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.merchantId} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-3 py-2">
                        {r.merchantName}
                        {r.accountGroupName && <span className="ml-2 text-[11px] text-indigo-600">({r.accountGroupName})</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.gstin || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{money(r.openingBalance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{r.totalDebit ? money(r.totalDebit) : ''}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{r.totalCredit ? money(r.totalCredit) : ''}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-blue-700">{money(r.closingDebit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{r.closingCredit ? money(r.closingCredit) : ''}</td>
                    </tr>
                  ))}
                  {rows.length > 0 && (
                    <tr className="border-t-2 bg-slate-50 dark:bg-slate-800 font-semibold">
                      <td className="px-3 py-2" colSpan={2}>Total</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(t?.openingBalance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(t?.totalDebit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(t?.totalCredit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-blue-700">{money(t?.closingDebit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{money(t?.closingCredit)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
